import { memo, useState, useEffect, useCallback } from 'react'
import { CalendarDaysIcon } from '@heroicons/react/24/outline'

import { vendorAPI } from '../../services/api/vendor'
import { createLogger } from '../../utils/logger'

const logger = createLogger('EntityScheduleSection')

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

/**
 * Formats a HH:MM:SS time string as 12h display, e.g. "9:00 AM"
 */
function formatTime(timeStr) {
  if (!timeStr) return ''
  const [h, m] = timeStr.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const hour = h % 12 || 12
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`
}

/**
 * EntityScheduleSection - Displays recurring schedule and upcoming events.
 * Renders nothing if the entity has no schedule data.
 * @idempotent @atomic
 */
const EntityScheduleSection = memo(function EntityScheduleSection({ entityId, t }) {
  const [schedules, setSchedules] = useState([])
  const [events, setEvents] = useState([])
  const [loaded, setLoaded] = useState(false)

  const fetchSchedule = useCallback(async () => {
    if (!entityId) return
    try {
      const res = await vendorAPI.getEntitySchedule(entityId)
      setSchedules(Array.isArray(res.data?.schedules) ? res.data.schedules : [])
      setEvents(Array.isArray(res.data?.upcoming_events) ? res.data.upcoming_events : [])
    } catch (err) {
      logger.debug('No schedule available', { entityId, err })
    } finally {
      setLoaded(true)
    }
  }, [entityId])

  useEffect(() => { fetchSchedule() }, [fetchSchedule])

  const isEmpty = schedules.length === 0 && events.length === 0

  // Group schedule slots by day
  const byDay = DAY_KEYS.map((_, idx) => schedules.filter(s => s.dayOfWeek === idx))
  const todayIdx = new Date().getDay()

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <CalendarDaysIcon className="w-4 h-4 text-primary flex-shrink-0" />
        <span className="text-xs font-semibold text-gray-700">{t('schedule.title', 'Schedule')}</span>
      </div>
      {!loaded && (
        <p className="text-xs text-gray-400 py-1">{t('common.loading', 'Loading…')}</p>
      )}
      {loaded && isEmpty && (
        <p className="text-xs text-gray-400 py-1">{t('schedule.noSchedule', 'No schedule posted yet')}</p>
      )}
      {loaded && !isEmpty && (
        <div className="space-y-3">
          {/* Weekly recurring schedule */}
          {schedules.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                {t('schedule.recurringTitle', 'Regular Schedule')}
              </p>
              <div className="space-y-1">
                {DAY_KEYS.map((dayKey, idx) => {
                  const daySlots = byDay[idx]
                  if (daySlots.length === 0) return null
                  const isToday = idx === todayIdx
                  return (
                    <div
                      key={dayKey}
                      className={`flex items-start gap-2 text-xs rounded px-2 py-1 ${isToday ? 'bg-primary/10 font-semibold' : ''}`}
                    >
                      <span className={`w-10 flex-shrink-0 ${isToday ? 'text-primary' : 'text-gray-500'}`}>
                        {t(`schedule.days.${dayKey}`, dayKey.charAt(0).toUpperCase() + dayKey.slice(1))}
                        {isToday && (
                          <span className="ml-1 text-primary text-xs">•</span>
                        )}
                      </span>
                      <div className="flex-1 space-y-0.5">
                        {daySlots.map(slot => (
                          <div key={slot.id} className="text-gray-700">
                            {formatTime(slot.startTime)} – {formatTime(slot.endTime)}
                            {slot.locationName && (
                              <span className="text-gray-400 ml-1">@ {slot.locationName}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Upcoming one-off events */}
          {events.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                {t('schedule.upcomingTitle', 'Upcoming Events')}
              </p>
              <div className="space-y-1">
                {events.map(ev => {
                  const dateStr = new Date(ev.eventDate).toLocaleDateString(undefined, {
                    month: 'short', day: 'numeric'
                  })
                  return (
                    <div key={ev.id} className="flex items-start gap-2 text-xs">
                      <span className="w-10 flex-shrink-0 text-gray-500 font-medium">{dateStr}</span>
                      <div className="flex-1 text-gray-700">
                        <span className="font-medium">{ev.title}</span>
                        <span className="text-gray-400 ml-1">
                          {formatTime(ev.startTime)} – {formatTime(ev.endTime)}
                        </span>
                        {ev.locationName && (
                          <span className="text-gray-400 ml-1">@ {ev.locationName}</span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
})

export default EntityScheduleSection
